import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { BracketEntry } from '@punditxi/db';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { StarsButton } from '../components/StarsButton';
import { track } from '../lib/analytics';
import { ApiError, api } from '../lib/api';
import { useUser } from '../lib/auth';
import { haptic } from '../lib/telegram';

const EARLY_BIRD_END = new Date('2026-06-01T23:59:00Z');
const BRACKET_LOCK = new Date('2026-06-10T23:59:00Z');

function pricing(now: Date) {
  if (now >= BRACKET_LOCK) return { stars: 0, closed: true, badge: null as string | null };
  if (now < EARLY_BIRD_END) return { stars: 300, closed: false, badge: 'Early bird' };
  return { stars: 400, closed: false, badge: null as string | null };
}

export function BracketHub() {
  const user = useUser();
  const bracket = useQuery({
    queryKey: ['bracket', 'me'],
    queryFn: () => api.bracketMe(),
    enabled: !!user.data?.hasBracketPremium,
  });

  if (user.isLoading) {
    return <div className="mx-auto max-w-md px-4 pt-10"><div className="h-32 animate-pulse rounded-xl bg-bg-card" /></div>;
  }

  if (!user.data?.hasBracketPremium) {
    return <Paywall />;
  }

  const groupCount = bracket.data?.groupPicks.length ?? 0;
  const koCount = bracket.data?.koPicks.length ?? 0;
  const entry = bracket.data?.entry;
  const locked = !!entry?.isLocked;

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <header className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl uppercase tracking-wide">Your bracket</h1>
        <span className="font-mono text-trophy-gold text-lg">{entry?.totalPoints ?? 0} pts</span>
      </header>

      {locked && <ReLockPanel entry={entry!} />}

      <StageCard
        title="Group stage"
        subtitle="12 groups · pick 1st / 2nd / 3rd"
        progress={`${groupCount}/${12 * 3}`}
        to="/bracket/builder?stage=groups"
      />
      <StageCard
        title="Knockouts"
        subtitle="R32 → R16 → QF → SF"
        progress={`${koCount}/30`}
        to="/bracket/builder?stage=ko"
      />
      <StageCard
        title="Final + Golden Boot"
        subtitle="Pick the champion"
        progress={entry?.championTeamId ? 'Locked' : 'Not yet'}
        to="/bracket/builder?stage=final"
      />

      <Link
        to="/bracket/confirmed"
        className="block text-center font-display-alt text-xs uppercase tracking-widest text-text-dim pt-2"
      >
        View confirmation ticket →
      </Link>
    </div>
  );
}

function ReLockPanel({ entry }: { entry: BracketEntry }) {
  const qc = useQueryClient();
  const [needsPaid, setNeedsPaid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tryFree = useMutation({
    mutationFn: () => api.bracketReLock(),
    onSuccess: async () => {
      haptic.success();
      await qc.invalidateQueries({ queryKey: ['bracket', 'me'] });
    },
    onError: (err) => {
      // 402 → no free re-locks left, paid path required
      if (err instanceof ApiError && err.status === 402) {
        setNeedsPaid(true);
        return;
      }
      haptic.error();
      setError(err instanceof Error ? err.message : 'Re-lock failed');
    },
  });

  return (
    <Card className="border-trophy-gold/40">
      <div className="font-chunky text-sm">Locked at WC kickoff</div>
      <div className="text-xs text-text-dim mt-1">
        Free re-locks remaining:{' '}
        <span className="font-mono text-trophy-gold">{entry.freeReLocksRemaining}</span>
        {entry.reLocksUsed > 0 && (
          <span className="ml-2 text-text-faint">· {entry.reLocksUsed} used</span>
        )}
      </div>

      {!needsPaid && (
        <Button
          className="mt-3"
          fullWidth
          variant={entry.freeReLocksRemaining > 0 ? 'gold' : 'primary'}
          disabled={tryFree.isPending}
          onClick={() => tryFree.mutate()}
        >
          {tryFree.isPending ? 'Working…' : 'Re-Lock to edit'}
        </Button>
      )}

      {needsPaid && (
        <div className="mt-3 space-y-2">
          <div className="text-xs text-text-dim">
            No free re-locks left. Unlock with Stars to edit again.
          </div>
          <StarsButton
            productCode="RE_LOCK"
            label="Pay to re-lock"
            stars={150}
            variant="gold"
            fullWidth
            invalidateKeys={[['bracket', 'me']]}
            onSuccess={() => {
              setNeedsPaid(false);
              void qc.invalidateQueries({ queryKey: ['bracket', 'me'] });
            }}
          />
        </div>
      )}

      {error && <div className="mt-2 text-crimson text-xs">{error}</div>}
    </Card>
  );
}

function StageCard({
  title,
  subtitle,
  progress,
  to,
}: { title: string; subtitle: string; progress: string; to: string }) {
  return (
    <Link to={to} className="block rounded-xl border border-border-base bg-bg-card p-4 active:scale-[0.99]">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-chunky text-base">{title}</div>
          <div className="text-xs text-text-dim mt-0.5">{subtitle}</div>
        </div>
        <div className="font-mono text-trophy-gold">{progress}</div>
      </div>
    </Link>
  );
}

function Paywall() {
  const { stars, closed, badge } = pricing(new Date());

  if (closed) {
    return (
      <div className="mx-auto max-w-md px-4 pt-10 space-y-4">
        <h1 className="font-display text-3xl uppercase">Bracket closed</h1>
        <p className="text-text-dim">
          Bracket Premium picks locked at 23:59 UTC on 10 June. Free predictions are still open for every match.
        </p>
        <Link to="/today"><Button variant="primary" fullWidth>See today's fixtures</Button></Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <header>
        <div className="font-display-alt text-xs uppercase tracking-widest text-trophy-gold">
          Bracket Premium
        </div>
        <h1 className="font-display text-4xl uppercase tracking-wide mt-1 leading-tight">
          Predict the
          <br />
          whole tournament
        </h1>
      </header>

      <div className="programme-stripe rounded-2xl border-2 border-trophy-gold/40 bg-bg-card p-5 space-y-3">
        <h2 className="font-display text-xl uppercase">What's in the ticket</h2>
        <ul className="space-y-2 text-sm">
          {[
            'Lock all 12 group winners, runners-up, and best thirds',
            'Build the full knockout bracket: R32 → R16 → QF → SF',
            'Pick the champion (30 pts) and Golden Boot (25 pts)',
            'Bracket leaderboard separate from match predictions',
            '4 free re-locks to revise picks during the tournament',
            'Daily fixture predictions stay free for everyone',
          ].map((it) => (
            <li key={it} className="flex gap-2">
              <span className="text-trophy-gold">▸</span>
              <span className="text-text-dim">{it}</span>
            </li>
          ))}
        </ul>
      </div>

      <Card className="text-center">
        <div className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
          Price
        </div>
        <div className="flex items-end justify-center gap-2 mt-1">
          <span className="font-display text-6xl text-trophy-gold leading-none">{stars}</span>
          <span className="font-display-alt text-2xl text-text-dim mb-1">⭐</span>
          {badge === 'Early bird' && (
            <span className="font-mono text-sm text-text-faint line-through ml-2 mb-1">400</span>
          )}
        </div>
        {badge && (
          <div className="mt-2 inline-block rounded-full bg-trophy-gold/20 border border-trophy-gold/40 px-3 py-1 font-display-alt text-[10px] uppercase tracking-widest text-trophy-gold">
            {badge} · ends 1 Jun
          </div>
        )}
        <div className="text-xs text-text-faint mt-3">
          Picks lock 23:59 UTC on 10 Jun. ~Max 250 bracket points.
        </div>
      </Card>

      <StarsButton
        productCode="BRACKET_PREMIUM"
        label="Pay"
        stars={stars}
        variant="gold"
        fullWidth
        invalidateKeys={[['me'], ['bracket', 'me']]}
        onSuccess={() => track('bracket_entry_paid', { stars_paid: stars, was_early_bird: badge === 'Early bird' })}
      />
      <Link to="/today" className="block text-center font-display-alt text-xs uppercase tracking-widest text-text-dim pt-2">
        Continue free →
      </Link>
    </div>
  );
}
